import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./admin.css";

import AdminShell from "./AdminShell";
import ADMIN_THEME from "./theme";
import { getDefaultSection, getNavItem, isBuiltSection } from "./navConfig";
import UsersPage from "./pages/UsersPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import P2PPage from "./pages/P2PPage";
import TransactionPage from "./pages/TransactionPage";
import TreasuryPage from "./pages/TreasuryPage";
import PredictionsPage from "./pages/PredictionsPage";
import LeaguesPage from "./pages/LeaguesPage";
import SystemPage from "./pages/SystemPage";
import ChatModerationPage from "./pages/ChatModerationPage";
import ReferralsPage from "./pages/ReferralsPage";
import PlaceholderPage from "./pages/PlaceholderPage";
import OverviewPage from "./pages/OverviewPage";
import "./styles/admin-overview.css";

import { logoutAllSessions } from "../utils/unifiedAuth";
import { selectUser, selectUserRole } from "../redux/slices/authSlice";
import {
  fetchAdminOverview,
  selectAdminOverview,
  selectAdminOverviewLoading,
  selectAdminOverviewLastFetched,
} from "./redux/adminOverviewSlice";

export default function AdminConsole() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sectionInit = useRef(false);
  const [activeSection, setActiveSection] = useState("overview");

  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const overview = useSelector(selectAdminOverview);
  const overviewLoading = useSelector(selectAdminOverviewLoading);
  const overviewLastFetched = useSelector(selectAdminOverviewLastFetched);

  useEffect(() => {
    dispatch(fetchAdminOverview());
  }, [dispatch]);

  useEffect(() => {
    if (sectionInit.current || !userRole) return;
    setActiveSection(getDefaultSection());
    sectionInit.current = true;
  }, [userRole]);

  const handleLogout = () => {
    logoutAllSessions(dispatch);
    navigate("/login");
  };

  const handleNavigate = useCallback(
    (section) => {
      const item = getNavItem(section, userRole);
      if (!item) return;
      setActiveSection(section);
    },
    [userRole]
  );

  const handleRefreshOverview = useCallback(() => {
    dispatch(fetchAdminOverview());
  }, [dispatch]);

  const content = useMemo(() => {
    switch (activeSection) {
      case "overview":
        return (
          <OverviewPage
            overview={overview}
            loading={overviewLoading}
            lastFetchedAt={overviewLastFetched}
            onRefresh={handleRefreshOverview}
            onNavigate={handleNavigate}
            userRole={userRole}
          />
        );
      case "users":
        return userRole === "super_admin" ? (
          <UsersPage theme={ADMIN_THEME} />
        ) : (
          <PlaceholderPage label="User Directory" description="Super admin access required." phase="Restricted" />
        );
      case "leaderboard":
        return <LeaderboardPage />;
      case "referrals":
        return <ReferralsPage />;
      case "chat-moderation":
        return userRole === "super_admin" ? (
          <ChatModerationPage />
        ) : (
          <PlaceholderPage label="Chat reports" description="Super admin access required." phase="Restricted" />
        );
      case "p2p":
        return <P2PPage />;
      case "transaction":
        return <TransactionPage />;
      case "treasury":
        return <TreasuryPage />;
      case "predictions":
        return <PredictionsPage />;
      case "leagues":
        return userRole === "super_admin" ? (
          <LeaguesPage />
        ) : (
          <PlaceholderPage label="Leagues" description="Super admin access required." phase="Restricted" />
        );
      case "system":
        return userRole === "super_admin" ? (
          <SystemPage />
        ) : (
          <PlaceholderPage label="Jobs & Queues" description="Super admin access required." phase="Restricted" />
        );
      default: {
        const item = getNavItem(activeSection, userRole);
        if (item && !isBuiltSection(activeSection)) {
          return <PlaceholderPage label={item.label} description={item.description} />;
        }
        return <P2PPage />;
      }
    }
  }, [activeSection, userRole, overview, overviewLoading, overviewLastFetched, handleRefreshOverview, handleNavigate]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        toastClassName="admin-toast"
        progressClassName="admin-toast-progress"
      />
      <AdminShell
        user={user}
        userRole={userRole}
        activeSection={activeSection}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        overview={overview}
        overviewLoading={overviewLoading}
        lastFetchedAt={overviewLastFetched}
        onRefreshOverview={handleRefreshOverview}
      >
        {content}
      </AdminShell>
    </>
  );
}
